import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  IconButton,
  Card,
  CardContent,
  InputAdornment,
  FormHelperText,
} from "@mui/material";
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  WhatsApp as WhatsAppIcon,
  LocationOn as LocationOnIcon,
  CameraAlt as CameraIcon,
  AttachFile as AttachFileIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useHistory, useLocation } from "react-router-dom";
import AlertModal from "../../components/AlertModal";

const AddVisit = () => {
  const history = useHistory();
  const location = useLocation();
  const leadData = location.state?.leadData || {};

  const [alertData, setAlertData] = useState({
    open: false,
    message: '',
    type: 'info',
  });

  const showAlert = (message, type = 'info') => {
    setAlertData({ open: true, message, type });
  };

  const [formData, setFormData] = useState({
    lead: leadData?.leadId || '',
    source: '',
    customer: leadData?.name || '',
    mobileNumber: leadData?.phone || '',
    whatsappNumber: leadData?.whatsapp || '',
    address: leadData?.location || '',
    project: '',
    interestedIn: '',
    budgetUpto: '',
    location: '',
    sizeArea: '',
    siteVisitBy: '',
    broker: '',
    visitDescription: '',
    remark1: '',
    remark2: '',
    inquiryStatus: '',
    nextFollowUpDate: '',
    followUpRemark: '',
    referenceName: '',
    referencePhone: '',
    selfies: [],
  });

  const projectOptions = [
    'Premium Plots',
    'Luxury Villas',
    'Modern Apartments',
    'Commercial Spaces',
    'Residential Complex',
  ];

  const interestedInOptions = [
    '1 BHK',
    '2 BHK',
    '3 BHK',
    '4 BHK',
    'Plot',
    'Villa',
    'Commercial',
  ];

  const sourceOptions = [
    'Website',
    'Reference',
    'Walk-in',
    'Social Media',
    'Advertisement',
    'Cold Call',
  ];

  const inquiryStatusOptions = [
    'Hot',
    'Warm',
    'Cold',
    'Interested',
    'Not Interested',
    'Follow-up Required',
  ];

  const [errors, setErrors] = useState({});

  const validateMobile = (mobile) => {
    const regex = /^[6-9]\d{9}$/;
    return regex.test(mobile);
  };

  const validateField = (name, value) => {
    let errorMsg = '';
    switch (name) {
      case 'source':
        if (!value) errorMsg = 'Source is required';
        break;
      case 'customer':
        if (!value || !value.trim()) errorMsg = 'Customer Name is required';
        break;
      case 'mobileNumber':
        if (!value || !value.trim()) {
          errorMsg = 'Mobile Number is required';
        } else if (!validateMobile(value.replace(/\D/g, ''))) {
          errorMsg = 'Mobile number must be 10 digits starting with 6-9';
        }
        break;
      case 'project':
        if (!value) errorMsg = 'Project is required';
        break;
      case 'interestedIn':
        if (!value) errorMsg = 'Interested In is required';
        break;
      case 'siteVisitBy':
        if (!value || !value.trim()) errorMsg = 'Site Visit By is required';
        break;
      default:
        break;
    }
    return errorMsg;
  };

  const handleFieldBlur = (name) => {
    const errorMsg = validateField(name, formData[name]);
    setErrors((prev) => ({
      ...prev,
      [name]: errorMsg,
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((prev) => ({
      ...prev,
      [name]: '',
    }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setFormData((prev) => ({
      ...prev,
      selfies: [...prev.selfies, ...files],
    }));
  };

  const handleRemoveSelfie = (index) => {
    setFormData((prev) => ({
      ...prev,
      selfies: prev.selfies.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = () => {
    const newErrors = {
      source: validateField('source', formData.source),
      customer: validateField('customer', formData.customer),
      mobileNumber: validateField('mobileNumber', formData.mobileNumber),
      project: validateField('project', formData.project),
      interestedIn: validateField('interestedIn', formData.interestedIn),
      siteVisitBy: validateField('siteVisitBy', formData.siteVisitBy),
    };

    setErrors(newErrors);

    const missingFields = [];
    const fieldLabels = {
      source: 'Source',
      customer: 'Customer Name',
      mobileNumber: 'Mobile Number',
      project: 'Project',
      interestedIn: 'Interested In',
      siteVisitBy: 'Site Visit By',
    };

    Object.keys(newErrors).forEach((field) => {
      if (newErrors[field]) {
        missingFields.push(fieldLabels[field]);
      }
    });

    if (missingFields.length > 0) {
      showAlert(`Please fill in all required fields:\n${missingFields.join(', ')}`, 'warning');

      const fieldOrder = [
        'source',
        'customer',
        'mobileNumber',
        'project',
        'interestedIn',
        'siteVisitBy',
      ];

      const firstErrorField = fieldOrder.find((field) => newErrors[field]);
      if (firstErrorField) {
        setTimeout(() => {
          const element = document.getElementById(`field-${firstErrorField}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      }
      return;
    }

    showAlert('Visit added successfully!', 'Success');
    setTimeout(() => {
      history.push('/app/siteVisit/SiteVisit');
    }, 1500);
  };

  const handleBack = () => {
    history.push('/app/siteVisit/SiteVisit');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 2, mb: 3, bgcolor: '#1976d2', color: '#fff' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={handleBack} sx={{ color: '#fff' }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Add Visit
          </Typography>
        </Box>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1976d2' }}>
            Details
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Lead"
                name="lead"
                value={formData.lead}
                onChange={handleChange}
                size="small"
                disabled
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4} id="field-source">
              <FormControl fullWidth size="small" required error={!!errors.source}>
                <InputLabel>Source</InputLabel>
                <Select
                  name="source"
                  value={formData.source}
                  onChange={handleChange}
                  onBlur={() => handleFieldBlur('source')}
                  label="Source"
                >
                  {sourceOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
                {errors.source && <FormHelperText>{errors.source}</FormHelperText>}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4} id="field-customer">
              <TextField
                fullWidth
                required
                label="Customer"
                name="customer"
                value={formData.customer}
                onChange={handleChange}
                onBlur={() => handleFieldBlur('customer')}
                error={!!errors.customer}
                helperText={errors.customer}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon sx={{ fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4} id="field-mobileNumber">
              <TextField
                fullWidth
                required
                label="Mobile Number"
                name="mobileNumber"
                value={formData.mobileNumber}
                onChange={handleChange}
                onBlur={() => handleFieldBlur('mobileNumber')}
                error={!!errors.mobileNumber}
                helperText={errors.mobileNumber}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon sx={{ fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="WhatsApp Number"
                name="whatsappNumber"
                value={formData.whatsappNumber}
                onChange={handleChange}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <WhatsAppIcon sx={{ fontSize: 18, color: '#25D366' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationOnIcon sx={{ fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4} id="field-project">
              <FormControl fullWidth size="small" required error={!!errors.project}>
                <InputLabel>Project</InputLabel>
                <Select
                  name="project"
                  value={formData.project}
                  onChange={handleChange}
                  onBlur={() => handleFieldBlur('project')}
                  label="Project"
                >
                  {projectOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
                {errors.project && <FormHelperText>{errors.project}</FormHelperText>}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4} id="field-interestedIn">
              <FormControl fullWidth size="small" required error={!!errors.interestedIn}>
                <InputLabel>Interested In</InputLabel>
                <Select
                  name="interestedIn"
                  value={formData.interestedIn}
                  onChange={handleChange}
                  onBlur={() => handleFieldBlur('interestedIn')}
                  label="Interested In"
                >
                  {interestedInOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
                {errors.interestedIn && <FormHelperText>{errors.interestedIn}</FormHelperText>}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Location"
                name="location"
                value={formData.location}
                onChange={handleChange}
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Size/Area"
                name="sizeArea"
                value={formData.sizeArea}
                onChange={handleChange}
                size="small"
                placeholder="e.g., 1200 sq.ft"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4} id="field-siteVisitBy">
              <TextField
                fullWidth
                required
                label="Site Visit By"
                name="siteVisitBy"
                value={formData.siteVisitBy}
                onChange={handleChange}
                onBlur={() => handleFieldBlur('siteVisitBy')}
                error={!!errors.siteVisitBy}
                helperText={errors.siteVisitBy}
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Broker"
                name="broker"
                value={formData.broker}
                onChange={handleChange}
                size="small"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Visit Description"
                name="visitDescription"
                value={formData.visitDescription}
                onChange={handleChange}
                size="small"
                multiline
                rows={3}
                placeholder="Describe the visit details..."
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Remark 1"
                name="remark1"
                value={formData.remark1}
                onChange={handleChange}
                size="small"
                multiline
                rows={2}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Remark 2"
                name="remark2"
                value={formData.remark2}
                onChange={handleChange}
                size="small"
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1976d2' }}>
            Follow Up
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Inquiry Status</InputLabel>
                <Select
                  name="inquiryStatus"
                  value={formData.inquiryStatus}
                  onChange={handleChange}
                  label="Inquiry Status"
                >
                  {inquiryStatusOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Next Follow Up Date"
                name="nextFollowUpDate"
                type="date"
                value={formData.nextFollowUpDate}
                onChange={handleChange}
                size="small"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>

            <Grid item xs={12} sm={12} md={4}>
              <TextField
                fullWidth
                label="Follow Up Remark"
                name="followUpRemark"
                value={formData.followUpRemark}
                onChange={handleChange}
                size="small"
              />
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1976d2' }}>
            Reference
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Reference Name"
                name="referenceName"
                value={formData.referenceName}
                onChange={handleChange}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon sx={{ fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Reference Phone"
                name="referencePhone"
                value={formData.referencePhone}
                onChange={handleChange}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon sx={{ fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1976d2' }}>
            Selfies
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Box sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              component="label"
              startIcon={<CameraIcon />}
              sx={{ textTransform: 'none' }}
            >
              Upload Selfies
              <input
                type="file"
                hidden
                multiple
                accept="image/*"
                onChange={handleFileChange}
              />
            </Button>
          </Box>

          {formData.selfies.length > 0 && (
            <Grid container spacing={2}>
              {formData.selfies.map((file, index) => (
                <Grid item xs={6} sm={4} md={3} key={index}>
                  <Card sx={{ position: 'relative' }}>
                    <CardContent sx={{ p: 1 }}>
                      <Box
                        sx={{
                          width: '100%',
                          height: 100,
                          bgcolor: '#f5f5f5',
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <AttachFileIcon sx={{ fontSize: 40, color: '#999' }} />
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          mt: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {file.name}
                      </Typography>
                      <IconButton
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          bgcolor: 'white',
                          boxShadow: 1,
                          '&:hover': {
                            bgcolor: '#ffebee',
                          },
                        }}
                        onClick={() => handleRemoveSelfie(index)}
                      >
                        <CloseIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
          <Button variant="outlined" onClick={handleBack}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
            sx={{ textTransform: 'none' }}
          >
            Save Visit
          </Button>
        </Box>
      </Paper>

      <AlertModal
        open={alertData.open}
        onClose={() => setAlertData({ ...alertData, open: false })}
        title={alertData.type}
        message={alertData.message}
        type={alertData.type.toLowerCase() === 'success' ? 'success' : alertData.type.toLowerCase() === 'error' ? 'error' : 'info'}
        disableRestoreFocus={true}
      />
    </Box>
  );
};

export default AddVisit;

